import axios from 'axios';
import React, { useContext, useState } from 'react';
import { newRegistrationContext, Step } from 'views/NewRegistration/NewRegistrationContext';

interface IProductContext {
    isSearching: boolean
    isSearchDone: boolean
    isValidating: boolean
    isValidationDone: boolean
    isQrCodeValid: boolean
    isUploading: boolean
    isUploaded: boolean

    searchProduct: (sku: string) => Promise<void>
    validateQrCode: (qrCode: string) => Promise<void>
    uploadReceipt: (file: any) => Promise<void>
}

export let productContext = React.createContext({} as IProductContext);

let { Provider } = productContext;

let ProductProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let service = useContext(newRegistrationContext);

    let [isSearching, setSearching] = useState(false);
    let [isSearchDone, setSearchDone] = useState(false);
    let [isValidating, setValidating] = useState(false);
    let [isQrCodeValid, setQrCodeValid] = useState(false);
    let [isValidationDone, setValidationDone] = useState(false);
    let [isUploading, setUploading] = useState(false);
    let [isUploaded, setUploaded] = useState(false);


    let searchProduct = async (sku: string) => {
        try {
            setSearching(true);
            setSearchDone(false);

            service.updateRegistration({
                ...service.registration,
                isQrCodeMandatory: false
            });

            setValidationDone(false);
            setQrCodeValid(false);

            let res = await axios.get('/api/Product/SearchProduct', { params: { searchTerm: sku } });

            if (res.data) {
                let specificRuleIdx = res.data.countrySpecificRules.findIndex((x: any) => x.countryCode === service.registration.userCountryCode);

                service.updateRegistration({
                    ...service.registration,
                    sku: res.data.sku,
                    unlimited: res.data.unlimited,
                    shortDescription: res.data.shortDescription,
                    isQrCodeMandatory: res.data.isQrMandatory,
                    warrantyExtensionMonths: specificRuleIdx === -1
                        ? res.data.warrantyExtensionMonths
                        : res.data.countrySpecificRules[specificRuleIdx].warrantyExtensionMonths,
                    productBrand: res.data.brandCode
                });

                if (res.data.isQrMandatory === false)
                    service.changeStep(Step.Dealer);
            } else {
                service.changeStep(Step.Product);
            }

            setSearchDone(true);

        } catch (err) {

        } finally {
            setSearching(false);
        }
    }

    let uploadReceipt = async (file: any) => {
        try {
            setUploading(true);
            setUploaded(false);

            let formData = new FormData();
            formData.append('file', file);

            let res = await axios.post('/api/Product/UploadReceipt', formData);
            service.updateRegistration({
                ...service.registration,
                receiptFilename: file.name,
                receiptId: res.data.id
            });

            setUploaded(true);

        } catch (err) { }
        finally {
            setUploading(false);
        }
    }

    let validateQrCode = async (qrCode: string) => {
        try {
            setValidating(true);
            setValidationDone(false);

            let request = {
                qrCode,
                sku: service.registration.sku
            }

            let res = await axios.get('/api/Product/CheckQr', { params: { ...request } });

            if (res.data === true) {
                service.updateRegistration({
                    ...service.registration,
                    qrCode
                });
                service.changeStep(Step.Dealer);
            } else {
                service.changeStep(Step.Product);
            }

            setQrCodeValid(res.data);
            setValidationDone(true);

        } catch (err) {
            setQrCodeValid(false);
        } finally {
            setValidating(false);
        }
    }


    return (
        <Provider
            value={{
                isSearching,
                isSearchDone,
                isValidating,
                isValidationDone,
                isQrCodeValid,
                isUploading,
                isUploaded,

                searchProduct,
                validateQrCode,
                uploadReceipt
            }}
        >
            {children}
        </Provider>
    )
}

export default ProductProvider;